import type { LucideIcon } from "lucide-react";
import { cn, tv, type VariantProps } from "@/lib/utils";

const selectionCardVariants = tv({
  base: "flex size-full flex-col items-center rounded-lg p-6 text-center transition-all",
  variants: {
    isSelected: {
      true: "ring-2 ring-rw-sub",
      false: "bg-rw-white",
    },
    disabled: {
      true: "cursor-not-allowed bg-transparent opacity-50",
      false:
        "hover:ring-2 hover:ring-rw-sub focus:outline-none focus:ring-2 focus:ring-rw-sub",
    },
  },
});

interface SelectionCardProps
  extends VariantProps<typeof selectionCardVariants> {
  label: string;
  description: string;
  IconComponent: LucideIcon;
  onClick: () => void;
  disabledMessage?: string;
}

export const SelectionCard: React.FC<SelectionCardProps> = ({
  label,
  description,
  IconComponent,
  isSelected,
  onClick,
  disabled,
  disabledMessage,
}) => {
  return (
    <div className={cn("relative", disabled && "cursor-not-allowed")}>
      <button
        type="button"
        onClick={onClick}
        aria-pressed={isSelected}
        disabled={disabled}
        className={selectionCardVariants({ isSelected, disabled })}
      >
        <div className="mb-4 flex size-16 items-center justify-center rounded-full">
          <IconComponent className="size-16 text-rw-sub" />
        </div>
        <h3 className="mb-2 text-xl font-medium">{label}</h3>
        <p className="text-sm text-rw-dark-gray">{description}</p>
      </button>

      {disabled && disabledMessage && (
        <div className="absolute inset-x-2 bottom-2 rounded border border-yellow-200 bg-yellow-50 px-2 py-1">
          <p className="text-center text-xs text-yellow-600">
            {disabledMessage}
          </p>
        </div>
      )}
    </div>
  );
};
